import { classifyScannedCode, parseSellerItemRef } from '@barso/shared';
import type { LocalSale } from './types';

/** Höchstzahl der Treffer im Kassenverlauf (die Liste soll auf dem Handy überschaubar bleiben). */
export const SALES_SEARCH_LIMIT = 50;
const MAX_WORDS = 3;

const fold = (text: string): string => text.toLocaleLowerCase('de-DE');

/**
 * Suche im Verlauf dieser Kasse, rein lokal (O6): Etikettencode (10 Ziffern oder übernommener fremder Code) trifft die
 * Zeile mit diesem Code; eine Zahl bis sechs Stellen trifft die Verkaufsnummer oder die Verkäufernummer einer Zeile; sonst
 * müssen alle Wörter (höchstens drei) in Bezeichnung oder Größe einer Zeile vorkommen. Ohne Suchtext zählt jeder Verkauf.
 * „17/5“ (Verkäufer-/Artikelnummer) trifft Zeilen mit dieser Nummer; das geht nur bei Verkäufen ab O3, ältere Zeilen kennen
 * die Artikelnummer nicht und sind über ihren Code zu finden. Die Reihenfolge bleibt wie geliefert (neueste zuerst), gekürzt auf `limit`.
 */
export function searchSales(
  sales: readonly LocalSale[],
  query: string,
  limit: number = SALES_SEARCH_LIMIT,
): LocalSale[] {
  const text = query.trim();
  if (text === '') return sales.slice(0, limit);
  const ref = parseSellerItemRef(text);
  if (ref !== null) {
    const refHits = sales.filter((sale) =>
      sale.lines.some(
        (line) => line.sellerNumber === ref.sellerNumber && line.itemNo === ref.itemNo,
      ),
    );
    if (refHits.length > 0) return refHits.slice(0, limit);
    // Kein Treffer über die Nummer: kann auch ein Code mit Bindestrich oder ein Wort sein, also weitersuchen
  }
  const scanned = classifyScannedCode(text);
  const number = /^\d{1,6}$/.test(text) ? Number(text) : null;
  const words = fold(text).split(/\s+/).slice(0, MAX_WORDS);
  const hits: LocalSale[] = [];
  for (const sale of sales) {
    const matches =
      (scanned !== null && sale.lines.some((line) => line.code === scanned.code)) ||
      (number !== null &&
        (sale.registerSeq === number || sale.lines.some((line) => line.sellerNumber === number))) ||
      sale.lines.some((line) => {
        const haystack = fold(`${line.title} ${line.size ?? ''}`);
        return words.every((word) => haystack.includes(word));
      });
    if (matches) {
      hits.push(sale);
      if (hits.length >= limit) break;
    }
  }
  return hits;
}
